/**
 * Does `profiles.profile_photo_url` name the object stored at `storagePath`?
 *
 * The column normally holds the bare storage path (migration
 * 20260205110847), but older rows hold an absolute URL to the same object,
 * for example `https://<ref>.supabase.co/storage/v1/object/public/dancer-media/<path>`
 * or a cdn.ballee.co copy. Both count as the same photo. The query string of a
 * signed URL is ignored.
 *
 * The SQL twin is the predicate in `trg_artist_media_privacy_side_effects`
 * (20261104170100). It does not percent-decode, so an absolute URL whose path
 * is encoded differently from `storage_path` matches here and not there.
 */
export function isProfilePhotoOf(
  photoUrl: string | null | undefined,
  storagePath: string | null | undefined,
): boolean {
  if (!photoUrl || !storagePath) return false;
  if (photoUrl === storagePath) return true;
  if (!/^https?:\/\//i.test(photoUrl)) return false;

  let pathname: string;
  try {
    pathname = decodeURIComponent(new URL(photoUrl).pathname);
  } catch {
    return false;
  }
  return pathname.endsWith(`/${storagePath}`);
}

/**
 * Is `photoUrl` the profile photo taken from this headshot row? A stored
 * headshot is matched on its `storage_path` (`isProfilePhotoOf`); a link-only
 * headshot (no `storage_path`) on its `external_url`, query string ignored.
 */
export function isProfilePhotoOfMedia(
  photoUrl: string | null | undefined,
  media: { storage_path: string | null; external_url: string | null },
): boolean {
  if (!photoUrl) return false;
  if (media.storage_path) return isProfilePhotoOf(photoUrl, media.storage_path);
  if (!media.external_url) return false;
  const withoutQuery = (url: string) => url.split('?')[0];
  return withoutQuery(photoUrl) === withoutQuery(media.external_url);
}
